import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Visibility,
  Store,
  Category,
  Inventory,
  LocalOffer,
  Info
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { appConfig } from '../../config/app';

const ViewProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { actions } = useApp();
  
  // State management
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${appConfig.api.baseURL}/products/${id}`);
        
        if (response.data.success) {
          setProduct(response.data.data);
        } else {
          setError('Product not found');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Failed to load product data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Helper: compute focusPrice for display (price/weight per KG)
  const getFocusPrice = (v) => {
    let wv = parseFloat(v?.weightValue);
    if (!wv || wv <= 0) {
      const m = String(v?.weight || '').match(/(\d+(?:\.\d+)?)/);
      wv = m ? parseFloat(m[1]) : 0;
    }
    const pr = parseFloat(v?.price) || 0;
    return wv > 0 ? (pr / wv).toFixed(2) : '-';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'draft': return 'warning';
      default: return 'default';
    }
  };

  // Get UI type display name
  const getUITypeDisplay = (uiType) => {
    switch (uiType) {
      case 'radio': return 'Radio Buttons';
      case 'checkbox': return 'Checkbox';
      case 'dropdown': return 'Dropdown';
      default: return uiType;
    }
  };

  // Helper function to format timestamp (Unix timestamp in milliseconds)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      let date;
      
      // Handle different timestamp formats
      if (timestamp.seconds) {
        // Legacy Firestore timestamp with seconds and nanoseconds
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        // Legacy Firestore timestamp object with toDate method
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        // ISO string
        date = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        // Unix timestamp in milliseconds (new format)
        date = new Date(timestamp);
      } else {
        return 'Unknown';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Unknown';
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/super-admin/products/list')}
        >
          Back to Products
        </Button>
      </Box>
    );
  }

  // Product not found
  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Product not found
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/super-admin/products/list')}
        >
          Back to Products
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/super-admin/products/list')}
          sx={{ mb: 2 }}
        >
          Back to Products
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {product.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View complete product information and details
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/super-admin/products/edit/${id}`)}
          >
            Edit Product
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Store sx={{ mr: 1 }} />
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Product Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{product.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Chip 
                  label={product.category?.charAt(0).toUpperCase() + product.category?.slice(1)} 
                  color="primary" 
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {product.description || 'No description provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip 
                  label={product.status?.charAt(0).toUpperCase() + product.status?.slice(1)} 
                  color={getStatusColor(product.status)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sort Order</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{product.sortOrder || 1}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Base Price</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{formatCurrency(product.price || 0)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Live Price</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{formatCurrency(product.livePrice || 0)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Gross Weight</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{product.grossWeight || 'Not specified'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Net Weight</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{product.netWeight || 'Not specified'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Metadata */}
          {product.metadata && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1 }} />
                Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.age || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">AKA (Also Known As)</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.aka || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Meta Title</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.metaTitle || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Meta Description</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.metaDescription || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Meta Keywords</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.metaKeywords || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Shelf Life</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.shelfLife || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Style</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{product.metadata.style || 'Not specified'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Variants */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Inventory sx={{ mr: 1 }} />
              Variants ({product.variants?.length || 0})
            </Typography>
            {product.variants && product.variants.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Weight</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Focus Price (/KG)</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Sort Order</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {product.variants.map((variant, index) => (
                      <TableRow key={index}>
                        <TableCell>{variant.weight}</TableCell>
                        <TableCell>{formatCurrency(variant.price)}</TableCell>
                        <TableCell>{getFocusPrice(variant) === '-' ? '-' : `${formatCurrency(parseFloat(getFocusPrice(variant)))}/KG`}</TableCell>
                        <TableCell>
                          <Chip label={variant.sku} size="small" />
                        </TableCell>
                        <TableCell>{variant.sortOrder}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No variants added for this product</Alert>
            )}
          </Paper>

          {/* Customizations */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <LocalOffer sx={{ mr: 1 }} />
              Customizations ({Object.keys(product.customizations || {}).length})
            </Typography>
            {product.customizations && Object.keys(product.customizations).length > 0 ? (
              <Stack spacing={2}>
                {Object.entries(product.customizations).map(([key, customization]) => (
                  <Card key={key} variant="outlined">
                    <CardHeader
                      title={customization.displayName || key}
                      subheader={
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip label={getUITypeDisplay(customization.uiType)} size="small" />
                          <Chip 
                            label={customization.required ? 'Required' : 'Optional'} 
                            color={customization.required ? 'error' : 'default'}
                            size="small" 
                          />
                          <Chip 
                            label={customization.enabled ? 'Enabled' : 'Disabled'} 
                            color={customization.enabled ? 'success' : 'default'}
                            size="small" 
                          />
                        </Box>
                      }
                    />
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Options:</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Option Name</TableCell>
                              <TableCell>Additional Price</TableCell>
                              <TableCell>Default</TableCell>
                              <TableCell>Sort Order</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customization.options?.map((option, optionIndex) => (
                              <TableRow key={optionIndex}>
                                <TableCell>{option.name}</TableCell>
                                <TableCell>{formatCurrency(option.additionalPrice || 0)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={option.isDefault ? 'Yes' : 'No'} 
                                    color={option.isDefault ? 'success' : 'default'}
                                    size="small" 
                                  />
                                </TableCell>
                                <TableCell>{option.sortOrder}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No customizations added for this product</Alert>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Product Image */}
          {product.image && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Product Image</Typography>
              <Avatar
                src={product.image}
                alt={product.name}
                sx={{ width: '100%', height: 200, borderRadius: 1 }}
                variant="rounded"
              />
            </Paper>
          )}

          {/* Quick Stats */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Quick Stats</Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Variants</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {product.variants?.length || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Customizations</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {Object.keys(product.customizations || {}).length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Base Price</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(product.price || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Live Price</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(product.livePrice || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Gross Weight</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {product.grossWeight || 'Not specified'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Net Weight</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {product.netWeight || 'Not specified'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Price Range</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {product.variants && product.variants.length > 0 ? (
                    `${formatCurrency(Math.min(...product.variants.map(v => v.price)))} - ${formatCurrency(Math.max(...product.variants.map(v => v.price)))}`
                  ) : (
                    'No variants'
                  )}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Created</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatTimestamp(product.createdAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatTimestamp(product.updatedAt)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Actions */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Actions</Typography>
            <Stack spacing={2}>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => navigate(`/super-admin/products/edit/${id}`)}
                fullWidth
              >
                Edit Product
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/super-admin/products/list')}
                fullWidth
              >
                Back to List
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewProduct; 